// lib/pdpaOptout.mjs
// Shared logic for the PDPA storage-notice opt-out flow:
// status derivation, attestation text, register event rows and CSV export.

export const PDPA_OPTOUT_ATTESTATION_VERSION = "2026-07-08.v1";

export const PDPA_OPTOUT_ATTESTATION_TEXT =
  "Our clinic has requested that the automatic PDPA data-storage notices " +
  "sent by ConnectLah to patients be disabled. We confirm that our clinic " +
  "informs patients, by its own means, that their personal data is " +
  "collected and stored, and that our clinic assumes sole responsibility " +
  "for the PDPA notification and consent obligations towards these patients.";

export const PDPA_NOTICE_STATUSES = [
  "active",
  "disabled_pending_confirmation",
  "disabled_confirmed",
];

const STATUS_LABELS = {
  active: "Notices active",
  disabled_pending_confirmation: "Disabled — awaiting clinic confirmation",
  disabled_confirmed: "Disabled — confirmed by clinic",
};

export function buildAttestationRecord() {
  return `[${PDPA_OPTOUT_ATTESTATION_VERSION}] ${PDPA_OPTOUT_ATTESTATION_TEXT}`;
}

export function getPdpaNoticeStatus(client) {
  if (!client) return "active";
  const enabled = client.pdpa_consent_messages_enabled;
  if (enabled !== false) return "active";
  if (client.pdpa_optout_confirmed_at) return "disabled_confirmed";
  return "disabled_pending_confirmation";
}

export function formatPdpaNoticeStatusLabel(status) {
  return STATUS_LABELS[status] || STATUS_LABELS.active;
}

export function isClinicConfirmationRequired(client) {
  return getPdpaNoticeStatus(client) === "disabled_pending_confirmation";
}

export function buildPdpaOptoutEvent({
  clientId,
  action,
  actorId,
  actorRole,
  actorName = null,
  attestation = null,
}) {
  if (clientId === null || clientId === undefined || clientId === "") {
    throw new Error("buildPdpaOptoutEvent: clientId is required");
  }
  if (!["messages_disabled", "messages_enabled", "clinic_confirmed"].includes(action)) {
    throw new Error(`buildPdpaOptoutEvent: unknown action "${action}"`);
  }
  if (!actorId) {
    throw new Error("buildPdpaOptoutEvent: actorId is required");
  }
  if (!["admin", "clinic"].includes(actorRole)) {
    throw new Error(`buildPdpaOptoutEvent: unknown actorRole "${actorRole}"`);
  }
  if (action === "clinic_confirmed" && !attestation) {
    throw new Error("buildPdpaOptoutEvent: clinic_confirmed requires the attestation text");
  }
  return {
    client_id: clientId,
    action,
    actor_id: actorId,
    actor_role: actorRole,
    actor_name: actorName,
    attestation,
  };
}

// Payload applied to the clients row when the admin flips the toggle.
export function buildClientsPdpaTogglePayload(enabled, { now = new Date() } = {}) {
  if (enabled) {
    return {
      pdpa_consent_messages_enabled: true,
      pdpa_optout_requested_at: null,
      pdpa_optout_confirmed_at: null,
      pdpa_optout_confirmed_by: null,
      pdpa_optout_confirmed_by_name: null,
      pdpa_optout_attestation: null,
    };
  }
  return {
    pdpa_consent_messages_enabled: false,
    pdpa_optout_requested_at: now.toISOString(),
    pdpa_optout_confirmed_at: null,
    pdpa_optout_confirmed_by: null,
    pdpa_optout_confirmed_by_name: null,
    pdpa_optout_attestation: null,
  };
}

// Payload applied to the clients row when the clinic confirms the opt-out.
export function buildClinicConfirmationPayload({ actorId, actorName, now = new Date() }) {
  if (!actorId) {
    throw new Error("buildClinicConfirmationPayload: actorId is required");
  }
  return {
    pdpa_optout_confirmed_at: now.toISOString(),
    pdpa_optout_confirmed_by: actorId,
    pdpa_optout_confirmed_by_name: actorName || null,
    pdpa_optout_attestation: buildAttestationRecord(),
  };
}

function csvEscape(value) {
  const text = value === null || value === undefined ? "" : String(value);
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

export const PDPA_REGISTER_CSV_HEADERS = [
  "client_id",
  "clinic",
  "status",
  "notices_enabled",
  "custom_message",
  "optout_requested_at",
  "confirmed_at",
  "confirmed_by",
  "attestation",
];

export function buildPdpaRegisterCsv(clients) {
  const lines = [PDPA_REGISTER_CSV_HEADERS.join(",")];
  for (const client of clients || []) {
    const status = getPdpaNoticeStatus(client);
    lines.push(
      [
        client.id,
        client.raison_sociale,
        status,
        client.pdpa_consent_messages_enabled === false ? "false" : "true",
        client.pdpa_consent_message || "",
        client.pdpa_optout_requested_at || "",
        client.pdpa_optout_confirmed_at || "",
        client.pdpa_optout_confirmed_by_name || client.pdpa_optout_confirmed_by || "",
        client.pdpa_optout_attestation || "",
      ]
        .map(csvEscape)
        .join(",")
    );
  }
  return lines.join("\r\n");
}
